<template>
  <div>
    <bid-list-add-new
        :isAddNewBidSidebarActive.sync="isAddNewBidSidebarActive"
        :typeOptions="typeOptions"
        :statusOptions="statusOptions"
        :customers="customers"
        :departments="departmentsOptions"
        :countries="countryOptions"
        @refetchData="refetchData"/>

    <!-- Filters -->
    <bids-list-filters
        :state-filter.sync="stateFilter"
        :state-options="stateOptions"
        :status-filter.sync="statusFilter"
        :status-options="statusOptions"
        :type-filter.sync="typeFilter"
        :type-options="typeOptions"
    />

    <!-- Table Container Card -->
    <b-card class="mb-0" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
              cols="12"
              md="6"
          >
            <label>Afficher</label>
            <v-select
                v-model="perPage"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                class="per-page-selector d-inline-block mx-50"
            />
            <label>Offres</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                  v-model="customerQuery"
                  class="d-inline-block mr-1"
                  placeholder="Client..."
              />
              <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  placeholder="Rechercher..."
              />
              <b-button
                  v-if="$can('create_bid', 'Offres')"
                  variant="primary"
                  @click="isAddNewBidSidebarActive = true"
              >
                <span class="text-nowrap">Ajouter Offre</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
          ref="refBidListTable"
          :fields="tableColumns"
          :items="fetchBids"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          class="position-relative"
          empty-text="Aucun enregistrements correspondants trouvés"
          hover
          primary-key="id"
          responsive
          show-empty
      >
        <!-- Column: Code -->
        <template #cell(offer_code)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                  :src="data.item.avatar"
                  :text="avatarText(data.item.offer_code)"
                  :variant="`light-${resolveBidTypeVariant(data.item.role)}`"
                  size="32"
              />
            </template>
            <b-link
                v-if="$can('read_bid', 'Offres')"
                :to="{ name: 'bids-view', params: { id: data.item.uid } }"
                class="font-weight-bold d-block text-nowrap"
            >
              {{
                data.item.offer_code
              }}
            </b-link>
            <small class="text-muted text-truncate">{{
                data.item.customer.name
              }}</small>
          </b-media>
        </template>

        <!-- Column: Brief -->
        <template #cell(brief)="data">
          <div
              class="text-truncate text-wrap"
              style="max-width: 150px; max-height: 60px"
          >
            <div v-html="data.item.brief"></div>
          </div>
        </template>

        <!-- Column: Type -->
        <template #cell(type)="data">
          <div class="text-nowrap">
            <feather-icon
                :class="`text-${resolveBidTypeVariant(data.item.type)}`"
                :icon="resolveBidTypeIcon(data.item.type)"
                class="mr-50"
                size="18"
            />
            <span class="align-text-top text-capitalize">{{
                data.item.type
              }} <span
                v-if="data.item.is_won"
                class="ml-1 text-success"
            >(Gagnée)</span
            ></span>
          </div>
        </template>

        <!-- Column: Created At -->
        <template #cell(created_at)="data">
          <div class="text-wrap">{{ formatDate(data.item.created_at) }}</div>
        </template>

        <!-- Column: Actions -->
        <template
            v-if="
            $can('read_bid', 'Offres') || $can('edit_bid', 'Offres')
          "
            #cell(actions)="data"
        >
          <b-dropdown
              v-if="!data.item.deleted_at && data.item.offer_code"
              :right="$store.state.appConfig.isRTL"
              no-caret
              variant="link"
          >
            <template #button-content>
              <feather-icon
                  class="align-middle text-body"
                  icon="MoreVerticalIcon"
                  size="16"
              />
            </template>
            <b-dropdown-item
                v-if="$can('read_bid', 'Offres')"
                :to="{ name: 'bids-view', params: { id: data.item.uid } }"
            >
              <feather-icon icon="FileTextIcon"/>
              <span class="align-middle ml-50">Détails</span>
            </b-dropdown-item>

            <b-dropdown-item
                v-if="$can('edit_bid', 'Offres')"
                :to="{ name: 'bids-edit', params: { id: data.item.uid } }"
            >
              <feather-icon icon="EditIcon"/>
              <span class="align-middle ml-50">Modifier</span>
            </b-dropdown-item>

            <b-dropdown-item
                v-if="$can('read_project', 'Projets') && data.item.is_won"
                :to="{ name: 'projects-view', params: { id: data.item.uid } }"
            >
              <feather-icon icon="GlobeIcon" />
              <span class="align-middle ml-50">Accéder au Projet</span>
            </b-dropdown-item>
          </b-dropdown>
          <b-badge
              v-else
              class="text-capitalize"
              pill
              variant="light-danger"
          >
            <feather-icon icon="XIcon"/>
          </b-badge>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
              cols="12"
              sm="6"
          >
            <span class="text-muted"
            >Affichage de {{ dataMeta.from }} à {{ dataMeta.to }} sur
              {{ dataMeta.of }} Offres</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
              cols="12"
              sm="6"
          >
            <b-pagination
                v-model="currentPage"
                :per-page="perPage"
                :total-rows="totalBids"
                class="mb-0 mt-1 mt-sm-0"
                first-number
                last-number
                next-class="next-item"
                prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18"/>
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18"/>
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
/* eslint-disable */
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import {ref, onMounted} from "@vue/composition-api";
import {avatarText, formatDate} from "@core/utils/filter";
import BidsListFilters from "./BidsListFilters.vue";
import useBidsList from "./useBidsList";
import BidListAddNew from "./BidListAddNew.vue";

export default {
  name: "BidsList",
  components: {
    BidsListFilters,
    BidListAddNew,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
  },
  setup() {
    const isAddNewBidSidebarActive = ref(false);
    const isLoading = ref(false);

    onMounted(() => {
      let props = ["customers", "countries", "departments", "bid_status", "bid_type"];
      fetchBidProperties(props);
    });

    const {
      fetchBids,
      fetchBidProperties,
      tableColumns,
      perPage,
      currentPage,
      totalBids,
      dataMeta,
      perPageOptions,
      customerQuery,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refBidListTable,
      refetchData,

      // UI
      resolveBidTypeVariant,
      resolveBidTypeIcon,
      resolveBidStatusVariant,
      bidStateClass,

      // Extra Filters
      stateFilter,
      typeFilter,
      statusFilter,
      customerOptions,
      stateOptions,
      countryOptions,
      departmentsOptions,
      statusOptions,
      typeOptions,
    } = useBidsList();

    return {
      isAddNewBidSidebarActive,
      isLoading,

      fetchBids,
      tableColumns,
      perPage,
      currentPage,
      totalBids,
      dataMeta,
      perPageOptions,
      searchQuery,
      customerQuery,
      sortBy,
      isSortDirDesc,
      refBidListTable,
      refetchData,

      // Filter
      formatDate,
      avatarText,

      // UI
      resolveBidTypeVariant,
      resolveBidTypeIcon,
      resolveBidStatusVariant,
      bidStateClass,

      stateOptions,
      typeOptions,
      statusOptions,
      countryOptions,

      // Extra Filters
      stateFilter,
      typeFilter,
      statusFilter,
      customers: customerOptions,
      departmentsOptions,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
